import * as p from '@clack/prompts'

import { parseDotenvFile, pullVercelEnvValue } from './env-pull.js'
import { runVercel } from './runner.js'
import type { ResendProvisionResult, VercelRunner } from './types.js'

/** Interactive provisioning waits on the user clicking through Vercel's prompts. */
const INTERACTIVE_PROVISION_TIMEOUT_MS = 600_000

/**
 * Interactively provision Resend email via `vercel integration add resend`
 * with inherited stdio, mirroring the Neon flow: the user accepts marketplace
 * terms and picks a plan in Vercel's own prompts. Uses `--no-env-pull` and then
 * pulls the RESEND_API_KEY into a temp file so BetterStart's `.env.local` is
 * never overwritten. Callers must stop any active spinner first — inherited
 * stdio and a clack spinner fight over the TTY.
 */
export async function provisionResendInteractive(
  runner: VercelRunner,
  cwd: string,
  options: { env?: NodeJS.ProcessEnv }
): Promise<ResendProvisionResult> {
  const add = await runVercel(runner, ['integration', 'add', 'resend', '--no-env-pull'], {
    cwd,
    mode: 'inherit',
    timeoutMs: INTERACTIVE_PROVISION_TIMEOUT_MS,
    env: options.env
  })

  if (!add.success) {
    return { failure: add.timedOut ? 'timeout' : 'provision-failed' }
  }

  const pullSpinner = p.spinner()
  pullSpinner.start('Retrieving the Resend API key')
  const apiKey = await pullVercelEnvValue(runner, cwd, readResendApiKeyFromDotenv, options.env)
  if (!apiKey) {
    pullSpinner.stop('No RESEND_API_KEY came back from Vercel')
    return { failure: 'env-pull-empty' }
  }
  pullSpinner.stop('Captured the Resend API key')

  return { apiKey }
}

/**
 * Extract the Resend API key from a pulled dotenv file. The Resend marketplace
 * integration writes `RESEND_API_KEY`; prefer that exact key, then any
 * `*RESEND_API_KEY` variant, then any `re_`-prefixed value.
 */
export function readResendApiKeyFromDotenv(filePath: string): string | undefined {
  const vars = parseDotenvFile(filePath)

  const direct = vars.get('RESEND_API_KEY')
  if (direct) return direct

  for (const [key, value] of vars) {
    if (/RESEND_API_KEY$/.test(key) && value) return value
  }
  for (const value of vars.values()) {
    if (value.startsWith('re_')) return value
  }
  return undefined
}
